import { ProjectEditionStatus } from '../helpers/project-status.helper';
import { ProjectCreate } from '../models';

export class ProjectCreateCleaner {
  private static readonly PRESERVED_FIELDS_BY_STATUS: Partial<
    Record<ProjectEditionStatus, ReadonlyArray<keyof ProjectCreate>>
  > = {
    [ProjectEditionStatus.STEP1]: [
      'client',
      'clientName',
      'title',
      'projectInfo',
      'editionStatus',
      'projectStatus',
    ],
    [ProjectEditionStatus.STEP2_BATTERY_CONFIGURATION_CHOICE]: [
      'client',
      'clientName',
      'title',
      'projectInfo',
      'editionStatus',
      'projectStatus',
      'isManualSizing',
    ],
    [ProjectEditionStatus.STEP2_BATTERY_CONFIGURATION_MANUAL]: [
      'client',
      'clientName',
      'title',
      'projectInfo',
      'editionStatus',
      'projectStatus',
      'isManualSizing',
      'manualSizing', // specifique
    ],
    [ProjectEditionStatus.STEP2_BATTERY_SIZING_TOOL]: [
      'client',
      'clientName',
      'title',
      'projectInfo',
      'editionStatus',
      'projectStatus',
      'isManualSizing',
      'sizingTool',
    ],
    [ProjectEditionStatus.STEP2_BATTERY_SIZING_TOOL_COMPUTED]: [
      'client',
      'clientName',
      'title',
      'projectInfo',
      'editionStatus',
      'projectStatus',
      'isManualSizing',
      'sizingTool',
      'sizingToolResults',
    ],
    [ProjectEditionStatus.STEP3_ENCLOSURE_INTERCONNECTS]: [
      'client',
      'clientName',
      'title',
      'projectInfo',
      'editionStatus',
      'projectStatus',
      'isManualSizing',
      'battery',
      'manualSizing',
      'sizingTool',
      'sizingToolResults',
      'orientation',
      'middlePoint',
      'connectingMethod',
      'supportType',
      'batterySpacing',
      'switchgearRequired',
      'switchgearPlacementMode',
      'selectedSwitchgear',
      'switchgearAnchor',
      'switchgearVerticalPlacement',
      'switchgearTierUsagePolicy',
      'calculMethod',
      'cableSection',
      'suppliers',
      'noSupport',
      'enclosurePropositions',
    ],
    [ProjectEditionStatus.STEP4_WIRING_PLAN_BOM]: [
      'client',
      'clientName',
      'projectInfo',
      'editionStatus',
      'projectStatus',
      'isManualSizing',
      'battery',
      'manualSizing',
      'sizingTool',
      'sizingToolResults',
      'enclosurePropositions',
      'orientation',
      'middlePoint',
      'connectingMethod',
      'supportType',
      'batterySpacing',
      'switchgearRequired',
      'switchgearPlacementMode',
      'selectedSwitchgear',
      'switchgearAnchor',
      'switchgearVerticalPlacement',
      'switchgearTierUsagePolicy',
      'calculMethod',
      'cableSection',
      'suppliers',
      'noSupport',
      'enclosurePropositions',
      'title',
      'purchaseOrder',
      'designerName',
      'revision',
      'comment',
      'version',
      'validatedBy',
      'bom',
    ],
  };

  static clean(project: Partial<ProjectCreate>): Partial<ProjectCreate> {
    const editionStatus = project.editionStatus;
    const resetProject = this.buildProjectCreateReset();

    if (!editionStatus) {
      return this.mergeWithDefaults(resetProject, project);
    }

    const preservedFields =
      this.PRESERVED_FIELDS_BY_STATUS[editionStatus as ProjectEditionStatus];

    if (!preservedFields) {
      return this.mergeWithDefaults(resetProject, project);
    }

    return this.mergeWithDefaults(
      resetProject,
      project,
      new Set(preservedFields),
    );
  }

  private static mergeWithDefaults(
    base: Partial<ProjectCreate>,
    project: Partial<ProjectCreate>,
    preservedFields?: Set<keyof ProjectCreate>,
  ): Partial<ProjectCreate> {
    const merged = {} as Record<
      keyof ProjectCreate,
      ProjectCreate[keyof ProjectCreate] | undefined
    >;

    (Object.keys(base) as Array<keyof ProjectCreate>).forEach((key) => {
      if (preservedFields?.has(key)) {
        return;
      }

      merged[key] = base[key] as ProjectCreate[keyof ProjectCreate];
    });

    (Object.keys(project) as Array<keyof ProjectCreate>).forEach((key) => {
      const value = project[key];

      if (typeof value !== 'undefined') {
        merged[key] = value as ProjectCreate[keyof ProjectCreate];
      }
    });

    return merged as Partial<ProjectCreate>;
  }

  private static buildProjectCreateReset(): Partial<ProjectCreate> {
    const reset: Record<keyof ProjectCreate, unknown> = {
      id: undefined,
      documentId: undefined,
      purchaseOrder: '',
      deliveryDate: null,
      deliveryLocation: '',
      deliveryClientName: '',
      client: null,
      folderNumber: '',
      sln: '',
      installationLocation: '',
      installationDate: null,
      battery: null,
      configurationQuantity: undefined,
      stringQuantity: undefined,
      quantityBatteryPerString: undefined,
      total: undefined,
      note: '',
      setupPrice: undefined,
      projectStatus: '',
      editionStatus: '',
      isArchived: false,
      deliverySlipFiles: [],
      invoiceFiles: [],
      paletteNumberFiles: [],
      planFile: null,
      estimateFile: null,
      estimateExcelFile: null,
      planSvgFile: null,
      sizingFile: null,
      commandFiles: [],
      middlePoint: null,
      connectingMethod: null,
      confSvg: null,
      orientation: null,
      bom: [],
      validatorName: '',
      designerName: '',
      schemaCreationDate: null,
      informationRemarks: '',
      cableSection: undefined,
      revision: '',
      caseForPdf: '',
      isManualSizing: null,
      sizingTool: null,
      manualSizing: null,
      clientName: '',
      projectInfo: '',
      sizingToolResults: [],
      suppliers: [],
      noSupport: null,
      calculMethod: null,
      supportType: null,
      batterySpacing: null,
      switchgearRequired: false,
      switchgearPlacementMode: null,
      selectedSwitchgear: null,
      switchgearAnchor: null,
      switchgearVerticalPlacement: null,
      switchgearTierUsagePolicy: null,
      title: '',
      comment: '',
      version: '',
      validatedBy: '',
      enclosurePropositions: [],
    };

    return reset as Partial<ProjectCreate>;
  }
}
